import React from 'react';
import { Box, Typography, Paper, useTheme, useMediaQuery, Avatar } from '@mui/material';
import DOMPurify from 'dompurify';

const MessageBubble = ({ message, isAgent, isInternal }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Paper
            elevation={1}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isAgent ? 'flex-start' : 'flex-end',
                marginBottom: 1.5,
                padding: isMobile ? 1.5 : 2,
                maxWidth: isMobile ? '90%' : '80%',
                minWidth: isMobile ? '60%' : 'auto',
                backgroundColor: isInternal ? theme.palette.warning.light : isAgent ? theme.palette.grey[100] : theme.palette.primary.main,
                borderRadius: isAgent ? '4px 20px 20px 20px' : '20px 4px 20px 20px',
                wordBreak: 'break-word',
                alignSelf: isAgent ? 'flex-start' : 'flex-end',
                transition: 'transform 0.5s ease, opacity 0.5s ease',
                '&:hover': {
                    transform: isMobile ? 'none' : 'translateY(-2px)',
                },
            }}
        >
            <Typography
                variant={isMobile ? 'body2' : 'body1'}
                sx={{
                    color: isAgent ? theme.palette.text.primary : "white",
                    whiteSpace: 'pre-wrap',
                    fontSize: isMobile ? '0.5rem' : '0.8rem',
                    '& a': {
                        color: isAgent ? theme.palette.primary.main : "white",
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    },
                }}
                dangerouslySetInnerHTML={{
                    __html: (DOMPurify.sanitize(message.body_html) || message.body).replace(/<a\s+(?![^>]*\btarget\s*=)/g, '<a target="_blank" rel="noopener noreferrer" '),
                }}
            />
        </Paper>
    );
};

const ChatBox = ({ comments, ticketDetails }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    function stringAvatar(name = '', isInternal = false, isAgent = false) {
        const words = name.trim().split(' ');
        const firstLetter = words[0]?.[0] || '';
        const secondLetter = words[1]?.[0] || '';

        return {
            sx: {
                backgroundColor: isInternal ? theme.palette.warning.light : isAgent ? theme.palette.grey[100] : theme.palette.primary.main,
                color: isAgent ? theme.palette.text.primary : "white",
                width: 32,
                height: 32,
                fontSize: "14px"
            },
            children: `${firstLetter}${secondLetter}`.toUpperCase(),
        };
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: isMobile ? 1 : 2,
                height: { xs: '500px', sm: '600px', md: '700px' },
                overflowY: 'auto',
                backgroundColor: 'background.default',
                borderTop: "solid 1px",
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'grey.100',
                    width: "5px"
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'grey.400',
                    width: "4px",
                    borderRadius: 3,
                },
            }}
        >
            {comments.map((comment, index) => (
                <div key={index} style={{ display: "flex", columnGap: "5px", justifyContent: comment?.agent_message ? "flex-start" : "flex-end" }}>
                    {comment?.agent_message && <Avatar {...stringAvatar(ticketDetails.assignee_name || "", comment?.internal || false, comment?.agent_message || false)} />}
                    <MessageBubble key={`message-${index}`} message={comment} isAgent={comment?.agent_message || false} isInternal={comment?.internal || false} />
                    {!comment?.agent_message && <Avatar {...stringAvatar(ticketDetails.requester_name || "")} />}
                </div>
            ))}
        </Box>
    );
};

export default ChatBox;